import { ThemeEvents } from '@theme/events';
import { insertPendingRow } from '@theme/cart-pending-row';
import { fetchDocument } from '@theme/ca-fetch';
import { bindScrollArrows } from '@theme/ca-scroll-arrows';
import { addToCart, preorderProperties } from '@theme/ca-cart-add';
import { VariantSheet, loadBody, prefetchBody } from '@theme/ca-variant-sheet';

/** <cart-upsell>: server-rendered tiers plus client cascade fill; quick-add collapses optimistically. */
(function () {
  if (customElements.get('cart-upsell')) return;

  const REDUCE_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Tags our own cart:update so onCartUpdate ignores the echo (a second fill() would race the first).
  const SOURCE_ID = 'cart-upsell';
  const OPEN_TIMEOUT_MS = 5000;

  const withTimeout = (promise, ms) =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('timeout')), ms);
      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });

  class CartUpsell extends HTMLElement {
    #filling = false;
    #refillQueued = false;
    #openToken = 0;

    connectedCallback() {
      this.track = this.querySelector('[data-upsell-track]');
      this.staticMode = this.hasAttribute('data-static');
      this.max = parseInt(this.dataset.max, 10) || 6;
      this.sectionId = this.dataset.sectionId || 'cart-upsell-results';
      this.pendingIds = this.pendingIds || new Set();
      this.stalePendingIds = this.stalePendingIds || new Set();
      this.recommendUrl = this.dataset.recommendUrl || '/recommendations/products';
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.controller = new AbortController();

      this.sheet = this.querySelector('[data-upsell-sheet]');
      this.mobileMQ = window.matchMedia('(max-width: 749px)');

      this.onClick = this.onClick.bind(this);
      this.onWarm = this.onWarm.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onSheetConfirm = this.onSheetConfirm.bind(this);
      this.onSheetClose = this.onSheetClose.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerdown', this.onWarm);
      this.addEventListener('focusin', this.onWarm);
      document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
      this.variantSheet = this.sheet
        ? new VariantSheet(this.sheet, { policy: 'skip', onConfirm: this.onSheetConfirm, onClose: this.onSheetClose })
        : null;

      this.bindNav();

      this.visible = false;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            this.visible = entries.some((e) => e.isIntersecting);
            if (this.visible) {
              this.arrows?.update();
              this.maybeNudge();
              this.prefetchOptions();
            }
          },
          { threshold: 0.3 }
        );
        this.io.observe(this);
      }

      this.fill();
    }

    disconnectedCallback() {
      this.#openToken += 1;
      if (this.controller) this.controller.abort();
      this.arrows?.disconnect();
      this.arrows = null;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerdown', this.onWarm);
      this.removeEventListener('focusin', this.onWarm);
      document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
      if (this.io) this.io.disconnect();
      this.cardIo?.disconnect();
      this.cardIo = null;
      this.variantSheet?.destroy();
      this.variantSheet = null;
    }

    onCartUpdate(event) {
      // Ignore the echo of our own add: removeCard already backfills via fill().
      if (event && event.detail && event.detail.sourceId === SOURCE_ID) return;
      this.pendingIds.forEach((id) => this.stalePendingIds.add(id));
      clearTimeout(this.refillTimer);
      this.refillTimer = setTimeout(() => this.fill(), 250);
    }

    /* ---- carousel nav ------------------------------------------------------ */

    bindNav() {
      this.arrows?.disconnect();
      this.arrows = null;
      if (!this.track) return;
      const prev = this.querySelector('.cart-upsell__nav .slideshow-control--previous');
      const next = this.querySelector('.cart-upsell__nav .slideshow-control--next');
      this.arrows = bindScrollArrows(this.track, prev, next, {
        initial: this.isRendered(),
        step: () => this.step(),
        onChange: ({ overflowing }) => {
          if (!prev || !next) return;
          const nav = this.querySelector('.cart-upsell__nav');
          if (nav) nav.style.display = overflowing ? '' : 'none';
        },
      });
    }

    isRendered() {
      return !this.closest('dialog:not([open])');
    }

    step() {
      const card = this.track && this.track.querySelector('.cart-upsell__card:not([hidden])');
      if (!card) return 160;
      const gap = parseFloat(getComputedStyle(card).marginInlineEnd) || 12;
      return card.offsetWidth + gap;
    }

    updateCount() {
      const el = this.querySelector('[data-upsell-count]');
      if (!el || !this.track) return;
      const n = this.track.querySelectorAll('.cart-upsell__card:not(.is-removing):not([hidden])').length;
      el.textContent = n > 0 ? String(n) : '';
    }

    /* ---- cascade fill ------------------------------------------------------ */

    async fill() {
      if (!this.track) return;
      if (this.staticMode) {
        this.afterFill();
        return;
      }

      // Re-entrancy guard: fill() is async and rebuilds `exclude` from the live DOM; overlapping calls coalesce.
      if (this.#filling) {
        this.#refillQueued = true;
        return;
      }
      this.#filling = true;

      try {
        const savedIds = document.querySelector('saved-for-later')
          ? await import('@theme/saved-for-later')
              .then((store) => store.list().map((entry) => String(entry.pid)))
              .catch(() => [])
          : [];
        this.stalePendingIds.forEach((id) => this.pendingIds.delete(id));
        this.stalePendingIds.clear();
        const cartIds = (this.dataset.cartIds || '').split(',').filter(Boolean);
        this.pendingIds.forEach((id) => {
          if (cartIds.includes(id)) this.pendingIds.delete(id);
        });
        this.primaryId = this.dataset.primaryId || '';
        const exclude = new Set([...cartIds, ...this.pendingIds]);
        this.track.querySelectorAll('.cart-upsell__card:not(.is-removing)').forEach((card) => {
          if (exclude.has(String(card.dataset.productId))) card.remove();
        });
        savedIds.forEach((id) => exclude.add(id));
        this.track
          .querySelectorAll('.cart-upsell__card:not([data-upsell-fallback])')
          .forEach((card) => exclude.add(String(card.dataset.productId)));

        const fallbacks = Array.from(this.track.querySelectorAll('.cart-upsell__card[data-upsell-fallback]'));
        const realCount = this.track.querySelectorAll('.cart-upsell__card:not([data-upsell-fallback])').length;
        const need = this.max - realCount;
        if (need <= 0) {
          this.afterFill();
          return;
        }

        const picked = [];

        const recentIds = this.recentlyViewedIds(exclude);
        if (recentIds.length) {
          const cards = await this.fetchCards(this.searchUrlFor(recentIds));
          this.collect(cards, exclude, picked, need - picked.length);
        }

        if (picked.length < need && this.primaryId) {
          const cards = await this.fetchCards(this.relatedUrlFor(this.primaryId));
          this.collect(cards, exclude, picked, need - picked.length);
        }

        picked.forEach((card) => {
          const fb = fallbacks.shift();
          if (fb) {
            this.track.insertBefore(card, fb);
            fb.remove();
          } else {
            this.track.appendChild(card);
          }
        });

        this.afterFill();
      } finally {
        this.#filling = false;
        if (this.#refillQueued) {
          this.#refillQueued = false;
          this.fill();
        }
      }
    }

    afterFill() {
      this.reveal();
      this.updateCount();
      if (this.isRendered()) this.arrows?.update();
      if (this.visible) {
        this.maybeNudge();
        this.prefetchOptions();
      }
    }

    optionsButtons() {
      return Array.from(this.querySelectorAll('[data-choose-options][data-sheet-url]'));
    }

    prefetchOptions() {
      if (!this.track || !('IntersectionObserver' in window)) return;
      this.cardIo ??= new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.cardIo.unobserve(entry.target);
            const button = entry.target.querySelector('[data-choose-options][data-sheet-url]');
            if (button) prefetchBody(button.dataset.sheetUrl);
          });
        },
        { root: this.track, threshold: 0.5 }
      );
      this.optionsButtons().forEach((button) => {
        const card = button.closest('.cart-upsell__card');
        if (card) this.cardIo.observe(card);
      });
    }

    onWarm(event) {
      const button = event.target.closest?.('[data-choose-options][data-sheet-url]');
      if (button) loadBody(button.dataset.sheetUrl).catch(() => {});
    }

    maybeNudge() {
      if (!this.track || !this.visible) return;
      if (REDUCE_MOTION.matches) return;
      try {
        if (sessionStorage.getItem('ca_cart_upsell_nudged')) return;
      } catch (e) {
        /* sessionStorage blocked — skip the nudge rather than repeat it */
        return;
      }
      const overflowing = this.track.scrollWidth - this.track.clientWidth > 8;
      if (!overflowing || this.track.scrollLeft > 2) return;
      try {
        sessionStorage.setItem('ca_cart_upsell_nudged', '1');
      } catch (e) {
        /* noop */
      }
      if (typeof this.track.animate !== 'function') return;
      this.track.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(-28px)', offset: 0.4 },
          { transform: 'translateX(0)' },
        ],
        { duration: 600, delay: 400, easing: 'ease-out' }
      );
    }

    collect(cards, exclude, picked, room) {
      for (const card of cards) {
        if (room <= 0) break;
        const id = String(card.dataset.productId || '');
        if (!id || exclude.has(id)) continue;
        exclude.add(id);
        picked.push(card);
        room -= 1;
      }
    }

    recentlyViewedIds(exclude) {
      let ids = [];
      try {
        ids = JSON.parse(localStorage.getItem('viewedProducts') || '[]');
      } catch (e) {
        ids = [];
      }
      return ids.map(String).filter((id) => id && !exclude.has(id));
    }

    searchUrlFor(ids) {
      const q = ids.map((id) => `id:${id}`).join(' OR ');
      return `${this.searchUrl}?q=${encodeURIComponent(q)}&resources[type]=product&section_id=${this.sectionId}`;
    }

    relatedUrlFor(productId) {
      return `${this.recommendUrl}?intent=related&product_id=${productId}&limit=${this.max}&section_id=${this.sectionId}`;
    }

    async fetchCards(url) {
      try {
        const doc = await fetchDocument(url, { signal: this.controller.signal });
        return Array.from(doc.querySelectorAll('.cart-upsell__card'));
      } catch (e) {
        return [];
      }
    }

    reveal() {
      if (!this.hasAttribute('hidden')) return;
      if (this.querySelector('.cart-upsell__card:not([hidden])')) this.removeAttribute('hidden');
    }

    /* ---- quick-add + exit animation ---------------------------------------- */

    onClick(event) {
      if (this.sheet && this.sheet.contains(event.target)) return;
      const choose = event.target.closest('[data-choose-options]');
      if (choose) {
        event.preventDefault();
        this.openSheet(choose);
        return;
      }

      const btn = event.target.closest('[data-add-to-cart]');
      if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      event.stopPropagation();

      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      this.addVariant({ variantId, card: btn.closest('.cart-upsell__card'), pill: null, button: btn });
    }

    onSheetConfirm({ variantId, pill, button, trigger }) {
      this.addVariant({ variantId, card: trigger ? trigger.closest('.cart-upsell__card') : null, pill, button });
    }

    addVariant({ variantId, card, pill, button: btn }) {
      const productId = card ? String(card.dataset.productId || '') : '';
      this.beginRemove(card, productId);

      const properties = preorderProperties(pill || btn);

      const pendingImg = (pill && pill.getAttribute('data-variant-image')) || '';
      const pendingCardImg = card && card.querySelector('img');
      const pendingSrc = pendingImg || (pendingCardImg ? pendingCardImg.currentSrc || pendingCardImg.src : '');
      const pendingRow = insertPendingRow(this, pendingSrc);

      addToCart({ id: variantId, quantity: 1, properties })
        .then(({ response, data: addData }) => {
          if (!response.ok || !addData) throw new Error('add failed');
          this.announce(btn.getAttribute('data-added-announce') || 'Item added to cart');
          // Tag sourceId/data.source so our onCartUpdate and cart-drawer-component skip this echo (cart:update shares CartAddEvent's DOM name).
          if (pendingRow) pendingRow.remove();
          document.dispatchEvent(
            new CustomEvent(ThemeEvents.cartUpdate, {
              bubbles: true,
              detail: {
                resource: null,
                sourceId: SOURCE_ID,
                data: { source: SOURCE_ID, sections: addData.sections },
              },
            })
          );
          this.commitRemove(card);
        })
        .catch(() => {
          if (pendingRow) pendingRow.remove();
          this.revertRemove(card, productId, btn);
        });
    }

    beginRemove(card, productId) {
      if (!card) return;
      if (productId) this.pendingIds.add(productId);
      card.__caCollapsed = false;
      const onEnd = () => {
        card.__caCollapsed = true;
      };
      if (REDUCE_MOTION.matches) {
        card.__caCollapsed = true;
      } else {
        card.addEventListener('transitionend', onEnd, { once: true });
        window.setTimeout(onEnd, 500);
      }
      card.classList.add('is-removing');
    }

    commitRemove(card) {
      if (!card) return;
      if (card.__caCollapsed || REDUCE_MOTION.matches) {
        this.removeCard(card);
        return;
      }
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        card.removeEventListener('transitionend', finish);
        this.removeCard(card);
      };
      card.addEventListener('transitionend', finish);
      window.setTimeout(finish, 500);
    }

    revertRemove(card, productId, btn) {
      if (card) {
        card.classList.remove('is-removing');
        card.__caCollapsed = false;
      }
      if (productId) this.pendingIds.delete(productId);
      if (btn && btn.isConnected) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = this.dataset.errorLabel || 'Try again';
      }
      this.announce('Could not add the item. Please try again.');
    }

    removeCard(card) {
      if (card && card.isConnected) card.remove();
      this.updateCount();
      if (this.isRendered()) this.arrows?.update();
      this.fill();
    }

    navigate(url) {
      window.location.href = url;
    }

    async openSheet(choose) {
      const card = choose.closest('.cart-upsell__card');
      const url = choose.dataset.sheetUrl;
      if (!card || !url || choose.getAttribute('aria-busy') === 'true') return;
      const productUrl = url.split('?')[0];
      if (!this.variantSheet) {
        this.navigate(productUrl);
        return;
      }

      const token = ++this.#openToken;
      choose.setAttribute('aria-busy', 'true');
      let body;
      try {
        body = await withTimeout(loadBody(url), OPEN_TIMEOUT_MS);
      } catch (error) {
        choose.removeAttribute('aria-busy');
        if (token === this.#openToken) this.navigate(productUrl);
        return;
      }
      choose.removeAttribute('aria-busy');
      if (token !== this.#openToken || !this.isConnected || !card.isConnected) return;

      this.variantSheet.open(body, {
        trigger: choose,
        confirmLabel: choose.dataset.confirmLabel,
        preorderLabel: this.dataset.preorderLabel,
      });
    }

    onSheetClose({ restored }) {
      if (restored) return;
      this.setAttribute('tabindex', '-1');
      this.focus({ preventScroll: true });
    }

    announce(message) {
      const live = this.querySelector('[data-upsell-live]');
      if (live) live.textContent = message;
    }
  }

  customElements.define('cart-upsell', CartUpsell);
})();
